import React, { memo, useEffect } from 'react';
import Modal from "react-modal";
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from "../../components/select";
import { useTranslation } from "react-i18next";

import styles from "../summary/summary.module.scss";
import useForm from '../../hooks/summary/useForm';
import validate from "../../validation/summaryValidationRules";
import SearchBookLists from './searchBook';
import LanguageSelector from '../../components/languageSelector';
import { useLanguage } from '../../context/languageContext';
import SummaryEditor from '../../components/textEditor/editor';
import Button from '../../components/button';
import PreviewImage from '../../components/image/previewImage';
import { convertToCloudFrontUrl, isBlankHtml } from '../../helper';
import GenerateOverviewButton from "./generateOverviewButton";
import TextStats, { StatLabel } from "./textStats";
import { SHOW_NOTABLE_QUOTES } from "./hiddenSections";
import { useAlreadyExists, useConfirm } from "../../components/confirmDialog";
import { includesFolded } from "../../helper/searchFold";

/**
 * The add form hides "why this book matters" and "key ideas", so their
 * required-checks have to be dropped here rather than in the shared rules —
 * editSummaryPopup imports the same validator and still shows both fields,
 * and blanking them there should stay blocked.
 */
const validateAddSummary = (values, language) => {
  const errors = validate(values, language) ?? {};
  delete errors.whyItMatters;
  delete errors.ideas;
  // Notable Quotes is hidden (see hiddenSections.js); a required field with no
  // input would block every submit.
  if (!SHOW_NOTABLE_QUOTES) delete errors.curatedQuotes;

  // The cover is the summary's face on every screen that lists it, and nothing
  // fills it in afterwards — a summary saved without one simply has none until
  // somebody notices. Required on the way in, where it costs one upload.
  //
  // Here rather than in the shared rules: a handful of summaries predate this
  // and have no cover of their own, and editing them must not be blocked by a
  // field their author never had to fill.
  if (!values.coverImage?.uploadedImageUrl) {
    errors.coverImage = "Please upload a cover image.";
    errors.message = "Form validation failed.";
  }

  // `validate` stamps a `message` key whenever anything failed, and useForm
  // gates submit on the number of keys — not on any of them being displayable.
  // Dropping the two field errors without dropping that left one key behind,
  // so a fully filled form could never be submitted and nothing on screen said
  // why: `message` is rendered next to no field.
  const remaining = Object.keys(errors).filter((key) => key !== "message");
  if (remaining.length === 0) delete errors.message;

  return errors;
};

const AddBookSummaryPopup = ({ closeModal, onSuccess }) => {
  const { t } = useTranslation();
  const { selectedLang, setSelectedLang, adminInfo, fallbackLang } = useLanguage();
  const showAlreadyExists = useAlreadyExists();
  const confirm = useConfirm();

  useEffect(() => {
    if (fallbackLang) {
      setSelectedLang(fallbackLang);
    }
  }, []);

  const handleCreateSummary = async (setIsLoading, setIsSubmit, values) => {
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/summary/add`, { values }, { headers });

      if (response.status === true) {
        onSuccess();
        closeModal();
        toast.success(`Summary of ${response?.data} added and converted into multiple languages success!`);
        setIsSubmit(false);
        setIsLoading(false);
      } else {
        toast.error(response?.message);
        setIsSubmit(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("handleCreateSummary catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
      setIsLoading(false);
      setIsSubmit(false);
    } finally {
      setIsLoading(false);
      setIsSubmit(false);
    }
  };

  const {
    inputRef,
    generatingRef,

    values,
    errors,
    isSubmit,
    isSubmitSummary,
    searchBookName,
    relatedBooksCount,
    booksData,
    summarizedBooks,
    bookSummaryId,
    editMediaError,
    fileInputRef,
    recommendeBooks,
    isConfirmModalVisible,
    setIsConfirmModalVisible,

    handleSelectLanguage,
    handleGetTranslationByLang,
    handleCreateAnIndexInChapters,
    handleRemoveAnIndexInChapters,
    handleUploadAudioFileInChapter,
    handleUploadAudioAndVideo,
    handleSearchTextChange,
    handleTextEditorChange,
    handleEditChapterValues,
    handleGetParticularBookDetail,
    handleAutoGenerateChapters,
    handleGenerateOverview,
    isGeneratingOverview,
    handleAddValues,
    handleUpdateTranslationField,
    handleUploadMedia,
    handleSubmit,
  } = useForm(handleCreateSummary, validateAddSummary, adminInfo);

  useEffect(() => {
    if (isConfirmModalVisible && !isSubmitSummary && generatingRef.current) {
      const timer = setTimeout(() => {
        setIsConfirmModalVisible(false);
        generatingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isSubmitSummary, isConfirmModalVisible]);


  return (
    <React.Fragment>
        <div className="container-fluid py-3">
          <div className={`box_wrapper ${styles.summaryFormContainer}`}>
            <div className={`d-flex justify-content-between fw-bold mb-3 ${styles.summaryFormHeader}`}>
              <h4>Add Summary</h4>
              <LanguageSelector onSelectLanguage={handleSelectLanguage} />
            </div>
            <div className={`modal_content_lg ${styles.summaryFormBody}`}>
              <div className={`row d-flex justify-content-between my-3 ${styles.summaryFormGrid}`}>
                <div className="col-sm-12 mb-3">
                  <label className="label">{t("summary.bookName")}: <span className='text-muted h5'>{handleGetTranslationByLang(selectedLang).bookName}</span></label>
                  <div className={styles.dropdownContainer}>
                    <input
                      className="px-3 py-3"
                      placeholder={`type to ${t("summary.searchBook")} title...`}
                      ref={inputRef}
                      type="text"
                      name="bookName"
                      onChange={(e) => handleSearchTextChange(e.target.value)}
                    />
                    {searchBookName !== "" && (booksData.length > 0 || summarizedBooks.length > 0) ? (
                      <div className={styles.dropdown}>
                        <SearchBookLists
                          onSuccess={(bookId) => handleGetParticularBookDetail(bookId)}
                          SearchBookLists={booksData.filter((item) => includesFolded(item.bookName, searchBookName))}
                          alreadySummarized={summarizedBooks.filter((item) => includesFolded(item.bookName, searchBookName))}
                        />
                      </div>
                    ) : (
                      searchBookName !== "" && (
                        <div className={styles.dropdown}>
                          <div className={styles.noBooksFound}>
                            <div className={styles.icon}>📚</div>
                            <p className={styles.message}>No books found matching your search "{searchBookName}"</p>
                            <p className={styles.subMessage}>Try searching with a different keyword or add a new book</p>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
                <div className="col-sm-12 mb-3">
                  <label className="label">{t("summary.tagline")}:</label>
                  <input
                    className="px-3 py-3 w-100"
                    type="text"
                    name="tagline"
                    placeholder={`${t("summary.enter")} ${t("summary.tagline")}...`}
                    value={handleGetTranslationByLang(selectedLang).tagline || ""}
                    onChange={(e) => handleTextEditorChange("tagline", e.target.value, selectedLang)}
                  />
                </div>
                <div className="col-sm-12 mb-3">
                  <label className="label">{t("summary.relatedBooks")}:</label>
                  <Select
                    className={`form-control-sm fw-medium p-0 w-100`}
                    name="relatedBooks"
                    isMulti
                    onChange={(selectedOption) => {
                      handleUpdateTranslationField(
                        "relatedBooks",
                        selectedOption.map((option) => ({ bookName: option.label, bookId: option.value, type: option.type })),
                        selectedLang
                      );
                    }}
                    value={handleGetTranslationByLang(selectedLang).relatedBooks.map((option) => ({ label: option.bookName, value: option.bookId }))}
                    options={recommendeBooks.filter((recommend) => recommend.value?.toString() !== values.bookSummaryId?.toString())}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    isDisabled
                  />
                  {errors && errors.relatedBooks && <p className="error-message d-block">{errors.relatedBooks}</p>}
                </div>
                {relatedBooksCount > 0 && (
                  <div className="col-sm-12 mb-3 d-flex justify-content-end">
                    <div className={styles.relatedBooksBadge}>
                      +<span className={styles.relatedBooksCount}>{relatedBooksCount}</span> more {relatedBooksCount > 1 ? 'books' : 'book'} you might like
                    </div>
                  </div>
                )}
                <div className="col-sm-12 col-md-4 mb-3">
                  <label className="label">{t("summary.authorName")}:</label>
                  <div className={`${styles.selectedValueContainer} ${!handleGetTranslationByLang(selectedLang).authorName || handleGetTranslationByLang(selectedLang).authorName.length === 0 ? styles.empty : ''}`}>
                    {handleGetTranslationByLang(selectedLang).authorName && handleGetTranslationByLang(selectedLang).authorName.length > 0 ? (
                      handleGetTranslationByLang(selectedLang).authorName.map((author, index) => (
                        <span key={index} className={styles.badge}>
                          {author.authorName}
                        </span>
                      ))
                    ) : (
                      <span>No authors selected</span>
                    )}
                  </div>
                  {errors && errors.authorName && <p className="error-message d-block">{errors.authorName}</p>}
                </div>
                <div className="col-sm-12 col-md-4 mb-3">
                  <label className="label">{t("summary.genre")}:</label>
                  <div className={`${styles.selectedValueContainer} ${!handleGetTranslationByLang(selectedLang).genre || handleGetTranslationByLang(selectedLang).genre.length === 0 ? styles.empty : ''}`}>
                    {handleGetTranslationByLang(selectedLang).genre && handleGetTranslationByLang(selectedLang).genre.length > 0 ? (
                      handleGetTranslationByLang(selectedLang).genre.map((genre, index) => (
                        <span key={index} className={styles.badge}>
                          {genre.genreName}
                        </span>
                      ))
                    ) : (
                      <span>No genres selected</span>
                    )}
                  </div>
                  {errors && errors.genre && <p className="error-message d-block">{errors.genre}</p>}
                </div>
                <div className="col-sm-12 col-md-4 mb-3">
                  <PreviewImage
                    label={t("book.coverImage")}
                    image={values.coverImage?.uploadedImageUrl}
                    mediaError={editMediaError}
                    error={errors?.coverImage}
                    uploadMedia={(e) => handleUploadMedia(e)}
                  />
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  <label className="label">{t("summary.uploadAudioNote")}: <span className='text-muted h6'>{t("summary.mention")}</span></label>
                  <input
                    className="file"
                    type="file"
                    name="images"
                    multiple
                    accept=".mp3, .wav, .ogg, .m4a"
                    ref={fileInputRef}
                    onChange={(e) => handleUploadAudioAndVideo(e)}
                    onClick={(e) => { e.target.value = null }}
                  />
                  {editMediaError && <p className="error-message d-block">{editMediaError}</p>}
                  {errors && errors.media && errors.media.audio && <p className="error-message d-block">{errors.media.audio}</p>}
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  <label className="label">{t("summary.uploadVideoNote")}: <span className='text-muted h6'>{t("summary.mention")}</span></label>
                  <input
                    className="file"
                    type="file"
                    name="images"
                    multiple
                    accept=".mp4, .webm, .ogg"
                    ref={fileInputRef}
                    onChange={(e) => handleUploadAudioAndVideo(e, selectedLang)}
                    onClick={(e) => { e.target.value = null }}
                  />
                  {editMediaError && <p className="error-message d-block">{editMediaError}</p>}
                  {errors && errors.media && errors.media.video && <p className="error-message d-block">{errors.media.video}</p>}
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  {handleGetTranslationByLang(selectedLang)?.media?.audio?.uploadedImageUrl && (
                    <audio controls className="mt-2 w-100">
                      <source src={convertToCloudFrontUrl(handleGetTranslationByLang(selectedLang).media.audio.uploadedImageUrl)} type="audio/mpeg" />
                    </audio>
                  )}
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  {handleGetTranslationByLang(selectedLang)?.media?.video?.uploadedImageUrl && (
                    <video controls width="250" className="mt-2">
                      <source src={convertToCloudFrontUrl(handleGetTranslationByLang(selectedLang).media.video.uploadedImageUrl)} type="video/mp4" />
                    </video>
                  )}
                </div>
                <div className="mb-2 d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-baseline gap-2">
                    <strong>Overview</strong>
                    <TextStats
                      parts={[
                        handleGetTranslationByLang(selectedLang).aboutTheBook,
                        SHOW_NOTABLE_QUOTES ? handleGetTranslationByLang(selectedLang).curatedQuotes : "",
                        handleGetTranslationByLang(selectedLang)
                          .actionableInsights,
                        handleGetTranslationByLang(selectedLang).shouldReadFor,
                        handleGetTranslationByLang(selectedLang)
                          .reflectionQuestions,
                      ]}
                    />
                  </div>
                  <GenerateOverviewButton
                    bookId={bookSummaryId}
                    selectedLang={selectedLang}
                    busy={isGeneratingOverview}
                    onGenerate={handleGenerateOverview}
                  />
                </div>
                <div className="mb-3">
                  <StatLabel
                    label={`${t("summary.aboutTheBook")}:`}
                    parts={[handleGetTranslationByLang(selectedLang).aboutTheBook]}
                  />
                  <SummaryEditor
                    initialValue={handleGetTranslationByLang(selectedLang).aboutTheBook}
                    onChange={(content) =>
                      // Normalised because this field alone is synced onto the
                      // BOOK (syncAboutTheBookToBook). Quill emits `<p><br></p>`
                      // for an emptied editor, and the backend treats that as
                      // content — it would translate the markup into all six
                      // languages and write it to the catalogue entry.
                      handleTextEditorChange('aboutTheBook', isBlankHtml(content) ? '' : content, selectedLang)
                    }
                  />
                  {errors && errors.aboutTheBook && <p className="error-message d-block">{errors.aboutTheBook}</p>}
                </div>
                {/* "Why this book matters" and "Key ideas" are hidden for now:
                    we are not collecting them on new summaries. The matching
                    required-checks are stripped by validateAddSummary above,
                    because leaving them on would block submit with an error the
                    admin has no field to fix. The shared rules themselves stay
                    intact — the edit form still shows both fields and should
                    still refuse to have them blanked.

                    The keys are still submitted, as the empty strings useForm
                    seeds them with — that is deliberate. updateSummary decides
                    whether to re-translate by comparing the stored value with
                    the incoming one, and "" against "" is equal, so nothing is
                    re-translated. Sending null instead would read as a change
                    against a stored "" and trigger a pointless translation of
                    an empty field on every save.

                    Restore both places together. */}
                {/* Notable Quotes: hidden, not removed — see hiddenSections.js. */}
                {SHOW_NOTABLE_QUOTES && (
                <div className="mb-3">
                  <StatLabel
                    label={`${t("summary.notableQuotes")}:`}
                    parts={[handleGetTranslationByLang(selectedLang).curatedQuotes]}
                  />
                  <SummaryEditor
                    initialValue={handleGetTranslationByLang(selectedLang).curatedQuotes}
                    onChange={(content) => handleTextEditorChange('curatedQuotes', content, selectedLang)}
                  />
                  {errors && errors.curatedQuotes && <p className="error-message d-block">{errors.curatedQuotes}</p>}
                </div>
                )}
                <div className="mb-3">
                  <StatLabel
                    label={`${t("summary.actionableInsights")}:`}
                    parts={[handleGetTranslationByLang(selectedLang).actionableInsights]}
                  />
                  <SummaryEditor
                    initialValue={handleGetTranslationByLang(selectedLang).actionableInsights}
                    onChange={(content) => handleTextEditorChange('actionableInsights', content, selectedLang)}
                  />
                  {errors && errors.actionableInsights && <p className="error-message d-block">{errors.actionableInsights}</p>}
                </div>
                <div className="mb-3">
                  <StatLabel
                    label={`${t("summary.shouldReadFor")}:`}
                    parts={[handleGetTranslationByLang(selectedLang).shouldReadFor]}
                  />
                  <SummaryEditor
                    initialValue={handleGetTranslationByLang(selectedLang).shouldReadFor}
                    onChange={(content) => handleTextEditorChange('shouldReadFor', content, selectedLang)}
                  />
                  {errors && errors.shouldReadFor && <p className="error-message d-block">{errors.shouldReadFor}</p>}
                </div>
                <div className="mb-3">
                  <StatLabel
                    label={`${t("summary.questionsThisBookForcesYouToAsk")}:`}
                    parts={[handleGetTranslationByLang(selectedLang).reflectionQuestions]}
                  />
                  <SummaryEditor
                    initialValue={handleGetTranslationByLang(selectedLang).reflectionQuestions}
                    onChange={(content) => handleTextEditorChange('reflectionQuestions', content, selectedLang)}
                  />
                  {errors && errors.reflectionQuestions && <p className="error-message d-block">{errors.reflectionQuestions}</p>}
                </div>

                <div className={styles.summaryChapterSection}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-baseline gap-2">
                      <label className="label mb-0">{t("summary.breakDown")}:</label>
                      <TextStats
                        parts={(
                          handleGetTranslationByLang(selectedLang)?.chapters ??
                          []
                        ).flatMap((c) => [c.chapterName, c.content])}
                      />
                    </div>
                    {bookSummaryId && (
                      <p className={styles.chapters} onClick={() => setIsConfirmModalVisible(true)}>Click to auto generate book's chapters</p>
                    )}
                  </div>
                  {handleGetTranslationByLang(selectedLang)?.chapters?.map((item, index) => (
                    <div
                      key={index}
                      className={`mb-3 px-3 py-3 rounded ${styles.summaryChapterCard}`}>
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-baseline flex-wrap gap-2">
                            <label className="label d-block mb-0">{t("summary.chapter")} {index + 1}:</label>
                            {/* This chapter alone — the breakdown total above
                                is every chapter together. */}
                            <TextStats parts={[item.chapterName, item.content]} />
                          </div>
                          {handleGetTranslationByLang(selectedLang)?.chapters?.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                if (await confirm({ title: `Delete chapter ${index + 1}?`, confirmLabel: "Delete" })) {
                                  handleRemoveAnIndexInChapters(index);
                                }
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <input
                          className="form-control px-3 py-3"
                          placeholder={`${t("summary.enter")} ${t("summary.chapter")} ${index + 1}...`}
                          type="text"
                          name="chapterName"
                          value={item.chapterName}
                          onChange={(e) => handleEditChapterValues("chapterName", e.target.value, index, selectedLang)}
                        />
                      </div>
                      <SummaryEditor
                        initialValue={item.content}
                        onChange={(content) => handleEditChapterValues("content", content, index, selectedLang)}
                      />
                    </div>
                  ))}
                </div>
                <div className="d-flex justify-content-end align-items-center gap-1">
                  <button type="button" className="btn btn-outline-dark" onClick={handleCreateAnIndexInChapters}>Add More</button>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-3 px-2 gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
              <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Please wait ... It might take a few minutes to complete..." text="Submit" />
            </div>
          </div>
        </div>
      <Modal
        isOpen={isConfirmModalVisible}
        onRequestClose={() => !isSubmitSummary && setIsConfirmModalVisible(false)}
        className="modal_body_sm"
        overlayClassName="modal_overlay"
      >
        <div className={`box_wrapper ${styles.confirmModalWrapper}`}>
          <div className={`d-flex justify-content-between fw-bold mb-3 ${styles.modalHeader}`}>
            <h4 className={styles.modalTitle}>Auto Generate Chapters</h4>
          </div>
          <div className={`modal_content ${styles.modalContent}`}>
            <div className={`mb-4 ${styles.messageContainer}`}>
              {isSubmitSummary ? (
                <p className={`mb-3 ${styles.messageText}`}>
                  Please wait ... It might take a few minutes to complete...
                </p>
              ) : (
                <p className={`mb-3 ${styles.messageText}`}>
                  Are you sure you want to auto generate book chapters using AI? This will create chapter summaries based on the book content.
                </p>
              )}
            </div>
            <div className={`d-flex justify-content-end gap-2 ${styles.buttonContainer}`}>
              <button
                type="button"
                className={`btn btn-outline-secondary ${styles.cancelBtn}`}
                onClick={() => setIsConfirmModalVisible(false)}
                disabled={isSubmitSummary}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-dark d-flex align-items-center justify-content-center gap-2 ${styles.generateBtn}`}
                onClick={async () => {
                  generatingRef.current = true;
                  try {
                    await handleAutoGenerateChapters(bookSummaryId);
                  } catch (error) {
                    generatingRef.current = false;
                    console.error("Error generating chapters:", error);
                  }
                }}
                disabled={isSubmitSummary}
              >
                {isSubmitSummary && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isSubmitSummary ? "Please wait ... It might take a few minutes to complete..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default memo(AddBookSummaryPopup)
